import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

// Materials
import { MatDialog } from '@angular/material/dialog';

// Esri
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import esriConfig from '@arcgis/core/config';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';

// Widgets
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Extent from '@arcgis/core/geometry/Extent';
import Search from '@arcgis/core/widgets/Search';
import LayerList from '@arcgis/core/widgets/LayerList';
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery';
import Expand from '@arcgis/core/widgets/Expand';
import Measurement from '@arcgis/core/widgets/Measurement';
import ScaleBar from '@arcgis/core/widgets/ScaleBar';
import CoordinateConversion from '@arcgis/core/widgets/CoordinateConversion';

import OAuthInfo from "@arcgis/core/identity/OAuthInfo";
import IdentityManager from "@arcgis/core/identity/IdentityManager";

// Info windows
import {
  AboutDialogComponent,
  AboutSplashDialogComponent,
} from '../app.component';

// Services
import { MapserviceService } from '../services/map-services.service';
import { UtilitiesService } from '../services/utilities-services.service';

import { environment } from '../../environments/environment';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit {
  // Router
  lat;
  lng;
  zoom;

  // Map constants
  map;
  view;
  webMap;
  webMapID; // Map ID
  studiesTable;

  // Login credentials
  loggedIn = false;

  // HTML styles
  mapWidth; // Map div width
  tableBtnDisplayType = 'none'; // Table button display type

  /* Map widgets */
  // Tables
  featureTable;
  tableActive = false;
  // Measure
  measurementActive = false;
  measurementAreaActive = false;
  measurementWidget;
  measurementAreaWidget;
  // Coordinate
  coordinateActive = false;
  coordinateWidget;
  coordinateExpand = false;
  // Printing
  printActive = false;
  userComms = 'None';
  basinNM = 'None';
  pointCollect = false;
  printMapLayout = false;
  basinPointCollect = false;
  printBasinLayout = false;
  basinStuff;

  
  // Set OAuth Defaults to the AppId in Portal
  private oAuthInfo: OAuthInfo = new OAuthInfo({
    appId: environment.appID,
    portalUrl: environment.portalURL,
    popup: false
  });

  constructor(
    private location: Location,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public dialog: MatDialog,
    private mapService: MapserviceService,
    private utilitiesService: UtilitiesService,
  ) {}

  ngOnInit() {
    // Set Portal URL
    esriConfig.portalUrl = environment.portalURL;
    esriConfig.assetsPath = '/assets'; // Assets path

    // this.initializeView();
    IdentityManager.registerOAuthInfos([this.oAuthInfo]);

    // Check if User has Authenticated
    IdentityManager.checkSignInStatus(this.oAuthInfo.portalUrl + "/sharing")
      .then(() => {
        this.loggedIn = true;
        
      })
      .catch(() => {
        this.loggedIn = false;
      })
      .finally(async () => {
        this.utilitiesService.loggedInStatus(this.loggedIn);
        const result = await this.initializeMap(this.loggedIn);
        if (!result) {
          this.loggedIn = false;
          this.utilitiesService.loggedInStatus(this.loggedIn);
          this.initializeMap(this.loggedIn);
        }

        // Splash screen
        if (!this.loggedIn) {
          this.openDialogWindow(AboutSplashDialogComponent);
        }
      });

    /* No Router Subscription */
    this.lat = this.route.snapshot.paramMap.get('lat');
    this.lng = this.route.snapshot.paramMap.get('lng');
    this.zoom = this.route.snapshot.paramMap.get('z');

    /* Router Subscription */
    this.route.paramMap.subscribe((params) => {
      this.lat = params.get('lat');
      this.lng = params.get('lng');
      this.zoom = params.get('z');
      this.updateLocation(this.lat, this.lng, this.zoom);
    });

    /* View subscription */
    this.mapService.view.subscribe((result) => {
      this.view = result;
    });

    /* Logged in subscription */
    // Purpose: Checks if the table is activated when logging in/out
    // If the table is active AND the user is logged in: minimize the map to 60% of the screen
    // Else: maximize the map to 100% of the screen
    this.utilitiesService.loggedIn.subscribe((result) => {
      this.loggedIn = result;
      if (this.tableActive === true && this.loggedIn === true) {
        this.mapWidth = '60vw';
      } else {
        this.mapWidth = '100vw';
      }
    });

    /* Toggle table subscription */
    // If true: minimize the map to 60% of the screen
    // Else if false: maximize the map to 100% of the screen
    this.utilitiesService.toggleTable.subscribe((result) => {
      this.tableActive = result;
      if (this.tableActive === true) {
        this.mapWidth = '60vw';
      } else {
        this.mapWidth = '100vw';
      }
    });

    /* Toggle print subscription */
    // If true: minimize the map to 60% of the screen
    // Else if false: maximize the map to 100% of the screen
    this.utilitiesService.togglePrint.subscribe((result) => {
      this.printActive = result;
      if (this.printActive === true) {
        this.mapWidth = '60vw';
      } else {
        this.mapWidth = '100vw';
      }
    });

    // // Splash screen
    // if (!this.loggedIn) {
    //   this.openDialogWindow(AboutSplashDialogComponent);
    // }
  }

  // Fetch a token for Portal so can load Webmaps from it without authentication
  async fetchToken() {
    try {
      let response = await fetch(environment.portalTokenUrl);
      let data = await response.json();
      return data;
    } catch (error) {
        console.error(error);
    }
  }

  // Application Routing
  // If no routing is selected, it is defined in the 'else' statement
  updateLocation(lat, lng, zoomLevel) {
    if (lat && lng && zoomLevel) {
      this.location.replaceState(
        '/lat/' + lat + '/lng/' + lng + '/z/' + zoomLevel
      );
    } else {
      this.zoom = 6;
      this.lat = 46.4055;
      this.lng = -94.2779;
    }
  }

  initializeView() {
    // Load the View with the Webmap and load Widgets
    
    // Add Webmap to New View
    this.view = new MapView({
      container: 'viewDiv',
      map: this.webMap,
      zoom: this.zoom,
      center: [this.lng, this.lat],
    });

    this.loadWidgets();

    reactiveUtils.watch(() => this.view.stationary, (stationary) => {
      if (this.view.center || this.view.zoom) {
        this.zoom = this.view.zoom;
        this.lat = this.view.center.latitude.toFixed(4);
        this.lng = this.view.center.longitude.toFixed(4);
        this.updateLocation(this.lat, this.lng, this.zoom);
      }
    });
    // Update view for other component subscriptions
    this.mapService.changeView(this.view);
  }

  async registerToken() {
    let token = await this.fetchToken();
    this.mapService.token = token.hosted_token.token;

    IdentityManager.registerToken({
      server: environment.portalURL,
      token: token.hosted_token.token,
      expires: (new Date()).getTime() + (token.expires_in * 1000)
    });
  }
  // Initialize Webmap
  async initializeMap(isLoggedIn: boolean) {
    // If logged in then set the Editor Web Map, otherwise load the Public one
    if (isLoggedIn) {
      this.webMapID = environment.editorWebMap;
    } 
    else {
      this.webMapID = environment.publicWebMap;
      await this.registerToken();
    }

    // Create New Webmap
    this.webMap = new WebMap({
      portalItem: {
        id: this.webMapID,
      },
    });

    try {
      await this.webMap.load();
      // this.webMap.load();
      console.log(this.mapService.token);
      if (this.mapService.token == "") { await this.registerToken(); }
      this.initializeView();
      if (this.webMap.portalItem.portal.user) {
        if (isLoggedIn) {  //this.webMap.portalItem.portal.user.fullName != "Admin Admin") {
          console.log(this.webMap.portalItem.portal.user.fullName);
          this.mapService.updateUsername(this.webMap.portalItem.portal.user.fullName);
        }
        else {
          this.mapService.updateUsername("");
        }
      }
      return true;
    }
    catch {
      alert("This account does not have access to the LFEO Editing Capabilities.  " +
        "Contact the DNR Floodplain Program staff for more information -- floodplain.dnr@state.mn.us");
      this.mapService.updateUsername("");
      return false;
    }
  }

  loadWidgets() {
    this.addSearchWidget();
    this.addLayerListWidget();
    this.addBasemapWidget();
    this.addInfoWidget();
    this.addTableWidget();
    this.addMeasurementWidget();
    this.addScalebarWidget();
    this.addCoordinateWidget();
    this.addPrintWidget();
    this.addLoginWidget();
  }
  
  // User login
  userLogin() {
    this.loggedIn = !this.loggedIn;
    this.utilitiesService.loggedInStatus(this.loggedIn);

    IdentityManager.destroyCredentials();
    this.mapService.token = "";
    if (this.loggedIn) {
      IdentityManager.getCredential(environment.portalURL + "/sharing");
    }

    // this.utilitiesService.loggedInStatus(this.loggedIn);

    if (!this.loggedIn) {
      window.location.reload();
      // this.initializeMap(this.loggedIn);
    }
  }

  /* Search Widget */
  addSearchWidget() {
    // Search symbol displayed when clicking from the list
    const searchSymbol = {
      type: 'simple-marker',
      style: 'square',
      color: 'blue',
      size: '14px',
      outline: {
        color: [255, 255, 0],
        width: 3,
      },
    };
    // Search Sources
    const searchSources = [
      {
        name: 'DNR Gazetteer',
        layer: new FeatureLayer({
          url: environment.dnrSearch
        }),
        searchFields: ['description'],
        exactMatch: false,
        outFields: ['description'],
        placeholder: 'example: Baldwin Lake',
        resultSymbol: searchSymbol,
      },
      {
        name: "ArcGIS World Geocoding Service",
        placeholder: 'Enter an address',
        singleLineFieldName: "SingleLine",
        outFields: ['Addr_type'],
        exactMatch: true,
        resultSymbol: searchSymbol,
        filter: {
              geometry: new Extent({
                xmin: -97.2304,
                ymin: 43.5008,
                xmax: -89.4919,
                ymax: 49.3877,
                spatialReference: {
                  wkid: 4326,
                },
              }),
            },
        url: "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer"
      }
    ];
    // Search widget
    const searchWidget = new Search({
      view: this.view,
      popupEnabled: false,
      includeDefaultSources: false,
      maxSuggestions: 20,
      minSuggestCharacters: 3,
      sources: searchSources,
    });
    // Add search widget to view
    this.view.ui.add(searchWidget, 'top-right');
  }

  /* LayerList Widget */
  addLayerListWidget() {
    const legend = new LayerList({
      view: this.view,
      listItemCreatedFunction(event) {
        const item = event.item;
        if (item.layer.type) {
          // item.actionsOpen = true, // Open actions (e.g., opacity) by default
          item.panel = {
            content: 'legend',
            open: true,
          };
        }
        // This sets an Action Section under the "..." button to change opacity on all layers in the layer list
        // Grouped items are still grouped for this function
        item.actionsSections = [
          [
            {
              title: 'Increase opacity',
              className: 'esri-icon-up',
              id: 'increase-opacity',
            },
            {
              title: 'Decrease opacity',
              className: 'esri-icon-down',
              id: 'decrease-opacity',
            },
          ],
        ];
      },
    });
    // Opacity action accessed by user
    legend.on('trigger-action', (event) => {
      // Get the layer
      const visibleLayer = event.item.layer;
      // Get the action ID to identify the action (increase or decrease)
      const id = event.action.id;
      // If increase opacity
      if (id === 'increase-opacity') {
        if (visibleLayer.opacity < 1) {
          visibleLayer.opacity += 0.1;
        }
        // Else if decrease opacity
      } else if (id === 'decrease-opacity') {
        if (visibleLayer.opacity > 0) {
          visibleLayer.opacity -= 0.1;
        }
      }
    });

    /* Add Legend or LayerList to the view */
    const layerListExpand = new Expand({
      view: this.view,
      content: legend,
      expandTooltip: 'Legend',
      group: 'top-right-widgets',
    });
    // Add layer list to view
    this.view.ui.add(layerListExpand, 'top-right');
  }
  /* Basemap Widget */
  addBasemapWidget() {
    // Basemap gallery
    const basemapGallery = new BasemapGallery({
      view: this.view,
      source: {
        portal: {
          url: 'https://www.arcgis.com',
        },
      },
    });
    // Basemap gallery in Expand tool
    const basemapExpand = new Expand({
      view: this.view,
      content: basemapGallery,
      expandTooltip: 'Basemap Layers',
      group: 'top-right-widgets',
    });
    // Close basemap widget if user selects one and their screen size is small
    basemapGallery.watch('activeBasemap', function () {
      const mobileSize =
        this.view.heightBreakpoint === 'xsmall' ||
        this.view.widthBreakpoint === 'xsmall';
      if (mobileSize) {
        basemapExpand.collapse();
      }
    });
    // Add basemap expand to view
    this.view.ui.add(basemapExpand, 'top-right');
  }
  /* Info Widget */
  addInfoWidget() {
    const infoBtnWidget = document.getElementById('infoBtn');
    this.view.ui.add(infoBtnWidget, 'top-right');
  }

  // Open dialog window (splash screen and login fail only)
  openDialogWindow(dialogName) {
    const dialogRef = this.dialog.open(dialogName, {
      maxWidth: '100vw',
      width: '1000px',
      maxHeight: '90vh',
    });
  }

  // Open the info window
  openAboutTextDialog() {
    const dialogRef = this.dialog.open(AboutDialogComponent, {
      maxWidth: '100vw',
      width: '1000px',
      maxHeight: '90vh',
    });
  }

  /* Table Widget */
  addTableWidget() {
    const tableBtnWidget = document.getElementById('tableBtn');
    this.view.ui.add(tableBtnWidget, 'top-right');
    // Show the table button when logged in
    // Otherwise hide the table button from users
    if (this.loggedIn) {
      this.tableBtnDisplayType = 'flex';
    } else {
      this.tableBtnDisplayType = 'none';
    }
  }
  // Toggle the table (routed via mapService to app-table component)
  toggleTable() {
    this.tableActive = !this.tableActive;
    this.utilitiesService.activateTable(this.tableActive);
  }

  /* Print Widget */
  addPrintWidget() {
    const printBtnWidget = document.getElementById('printBtn');
    this.view.ui.add(printBtnWidget, 'top-right');
  }
  // Toggle the table (routed via mapService to app-table component)
  togglePrint() {
    this.printActive = !this.printActive;
    this.utilitiesService.activatePrint(this.printActive);
    this.view.graphics.removeAll();
    let properties = {};
    properties['Module'] = 'map.components.ts';
  }

  /* Measure Widget */
  // Note: Widget loads in bottom right, but buttons reside from html/scss in upper right
  addMeasurementWidget() {
    /* *** LINE *** */
    // Distance widget button
    const distanceBtnWidget = document.getElementById('distanceBtn');
    this.view.ui.add(distanceBtnWidget, 'top-right');
    // Measure widget functionality
    this.measurementWidget = new Measurement({
      view: this.view,
      linearUnit: 'us-feet',
    });
    /* *** AREA *** */
    // Distance widget button
    const distanceAreaBtnWidget = document.getElementById('distanceAreaBtn');
    this.view.ui.add(distanceAreaBtnWidget, 'top-right');
    // Measure widget functionality
    this.measurementAreaWidget = new Measurement({
      view: this.view,
      areaUnit: 'square-us-feet',
    });
    // If state changes, keep measure tool activated
    if (this.measurementActive) {
      this.measurementWidget.activeTool = 'distance';
    } else if (this.measurementAreaActive) {
      this.measurementAreaWidget.activeTool = 'area';
    }
    // Add Measure tools to the view
    this.view.ui.add(
      [this.measurementWidget, this.measurementAreaWidget],
      'bottom-left'
    );
  }

  // Change Measurement, depending on user control
  changeMeasurementType(type: string) {
    // Clears coordinate widget, if active
    if (this.coordinateActive) {
      this.activateCoordinateWidget();
    }
    // Activate measurement depending on user selection (line/distance -or- polygon/area)
    if (type === 'distance') {
      this.measurementActive = !this.measurementActive;
      this.measurementAreaActive = false;
      if (this.measurementActive === true) {
        this.measurementAreaWidget.clear();
        this.measurementWidget.activeTool = 'distance';
      } else {
        this.measurementWidget.clear();
      }
    } else if (type === 'area') {
      this.measurementAreaActive = !this.measurementAreaActive;
      this.measurementActive = false;
      if (this.measurementAreaActive === true) {
        this.measurementWidget.clear();
        this.measurementAreaWidget.activeTool = 'area';
      } else {
        this.measurementAreaWidget.clear();
      }
    }
  }

  /* Scale Widget */
  addScalebarWidget() {
    const scalebarWidget = new ScaleBar({
      view: this.view,
    });
    this.view.ui.add(scalebarWidget, 'bottom-right');
  }
  /* Coordinate Widget */
  addCoordinateWidget() {
    const coordinateConversionBtnWidget = document.getElementById(
      'coordinateConversionBtn'
    );
    this.view.ui.add(coordinateConversionBtnWidget, 'top-right');
    this.coordinateWidget = new CoordinateConversion({
      view: this.view,
      visibleElements: {
        settingsButton: false,
      },
      mode: 'live',
    });
  }

  // Activate the Coordinate widget
  activateCoordinateWidget() {
    this.coordinateExpand = !this.coordinateExpand;
    this.coordinateActive = !this.coordinateActive;
    // If the coordinate tool is active
    if (this.coordinateExpand) {
      this.coordinateWidget.mode = 'capture';
      this.view.ui.add(this.coordinateWidget, 'bottom-left');
      // Clears measurement widgets
      this.measurementAreaActive = false;
      this.measurementActive = false;
      this.measurementAreaWidget.clear();
      this.measurementWidget.clear();
    } else {
      this.coordinateWidget.mode = 'live';
      try {
        this.view.ui.remove(this.coordinateWidget);
      } catch (err) {
        // console.log(err); // Testing
      }
    }
  }

  /* Login Widget */
  addLoginWidget() {
    const loginBtnWidget = document.getElementById('loginBtn');
    this.view.ui.add(loginBtnWidget, 'top-right');
  }
}
